<template>
    <rkm-modal
        extra-large
        ref="Modal"
        title="Solicitações"
    >
        <rkm-form ref="Form">
            <rkm-table
                v-if="solicitacoesData"
                :fields="fields"
                :items="solicitacoesData.model.solicitacoesItens"
                hide-filters
                hide-pagination
                sort-by="dataSolicitacao"
                sort-desc
            >
                <template v-slot:cell(status)="{ item }">
                    <rkm-form-select
                        v-if="item.isExternoNaoRegulado"
                        :options="solicitacoesData.form.status.filter(s => item.statusPermitidos.includes(s.id))"
                        :validations="getItemValidations(item).status"
                        name="Status"
                        text-field="nome"
                        v-model="item.status"
                        value-field="id"
                    />

                    <template v-else>{{ item.status }}</template>
                </template>

                <template v-slot:cell(dataAgendada)="{ item }">
                    <rkm-form-date-picker
                        v-if="item.isExternoNaoRegulado"
                        :min-date="solicitacoesData.additionals.dataAtual"
                        :validations="getItemValidations(item).dataAgendada"
                        name="Data agendada"
                        v-model="item.dataAgendada"
                    />

                    <template v-else>{{ item.dataAgendada ? formatDate(item.dataAgendada) : null }}</template>
                </template>

                <template v-slot:cell(unidadeExecutora)="{ item }">
                    <rkm-form-select
                        v-if="item.isExternoNaoRegulado"
                        :options="solicitacoesData.form.unidadesExecutoras"
                        :validations="getItemValidations(item).unidadeExecutora"
                        name="Unidade executora"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="item.unidadeExecutora"
                        value-field="id"
                    />

                    <template v-else>{{ item.unidadeExecutora }}</template>
                </template>

                <template v-slot:cell(ocupacaoExecutora)="{ item }">
                    <rkm-form-input-autocomplete
                        v-if="item.isExternoNaoRegulado"
                        :repository="repositories.ocupacao"
                        :request-query-string-params="{ procedimento_id: item.procedimentoId }"
                        :search-fields="[
                            {key: 'ocupacao.codigo', searchable: true},
                            {key: 'ocupacao.nome', searchable: true},
                        ]"
                        :text-field="(item) => item.codigo"
                        :validations="getItemValidations(item).ocupacaoExecutora"
                        name="CBO executora"
                        class="mb-2"
                        v-model="item.ocupacaoExecutora"
                    >
                        <template #itemLabel="{ item }">
                            {{ item.codigo ? `${item.codigo} - ${item.nome}` : null }}
                        </template>
                    </rkm-form-input-autocomplete>

                    <template v-else>{{ item.ocupacaoExecutora }}</template>
                </template>

                <template v-slot:cell(usuarioExecutor)="{ item }">
                    <rkm-form-input
                        v-if="item.isExternoNaoRegulado"
                        :validations="getItemValidations(item).usuarioExecutor"
                        name="Profissional executor"
                        uppercase
                        v-model="item.usuarioExecutor"
                    />

                    <template v-else>{{ item.usuarioExecutor }}</template>
                </template>

                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        @click="$refs.ModalDetalhe.open(item)"
                        color="primary"
                        small
                        title="Exibir detalhes"
                    >
                        <rkm-icon icon="bars" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-form>

        <ModalDetalhe ref="ModalDetalhe" />

        <template v-slot:footer="{cancel}">
            <rkm-button
                :disabled="!!loading"
                @click="cancel"
                color="secondary"
            >
                Fechar
            </rkm-button>

            <rkm-button
                v-if="solicitacoesData && solicitacoesData.model.solicitacoesItens.some(s => s.isExternoNaoRegulado)"
                :disabled="!!loading"
                @click="$refs.Form.validate(updateSolicitacoes)"
                color="primary"
            >
                Salvar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Components.
import ModalDetalhe from './components/detalhe/Modal'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Mixins.
import { repository, error, formats, file, stringCase } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import soapSolicitacaoExameItemStatus from '@/enums/ambulatorio/soapSolicitacaoExameItemStatus'

export default {
    components: { ModalDetalhe },
    computed: {
        ...mapGetters({ loading: 'getLoading' }),
        fields() {

            return [
                { key: 'dataSolicitacao', label: 'Data da solicitação', formatter: v => this.formatDate(v), sortable: true },
                { key: 'exameDescricao', label: 'Exame', sortable: true },
                { key: 'status', label: 'Status' },
                { key: 'dataAgendada', label: 'Data agendada' },
                { key: 'unidadeExecutora', label: 'Unidade executora' },
                { key: 'ocupacaoExecutora', label: 'CBO executora' },
                { key: 'usuarioExecutor', label: 'Profissional executor' },
                { key: 'actions', label: 'Ações', class: 'col-min text-center' },
            ]
        },
    },
    created() {
        this.repositories = {
            monitoramentoProtocolo: this.getConectaSusRepository('regulacao.monitoramentoProtocolo'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao')
        }
    },
    data: () => ({
        acompanhamentoOci: null,
        solicitacoesData: null,
        enums: { soapSolicitacaoExameItemStatus },
        others: { moment }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getItemValidations(item) {

            const isStatusAgendamento = item.status === this.enums.soapSolicitacaoExameItemStatus.AGENDADO
                || item.status === this.enums.soapSolicitacaoExameItemStatus.REAGENDADO

            const isStatusRealizado = item.status === this.enums.soapSolicitacaoExameItemStatus.REALIZADO

            return {
                status: { required: true },
                dataAgendada: { required: isStatusAgendamento || isStatusRealizado },
                unidadeExecutora: { required: isStatusAgendamento || isStatusRealizado },
                ocupacaoExecutora: { required: isStatusAgendamento || isStatusRealizado },
                usuarioExecutor: { max: 100 },
            }
        },
        open(item) {

            this.acompanhamentoOci = item

            this.solicitacoesData = null

            this.load()

            this.$refs.Modal.show()
        },
        load() {

            this.setLoading()

            this.repositories.monitoramentoProtocolo.getSolicitacoes(this.acompanhamentoOci.id)
                .then(({ data }) => this.solicitacoesData = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        updateSolicitacoes() {

            this.setLoading()

            this.repositories.monitoramentoProtocolo.updateSolicitacoes(this.prepareData())
                .then(() => {

                    this.showSaveNotification()
                    this.load()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData() {

            const solicitacoes = this.solicitacoesData.model.solicitacoesItens.filter(s => s.isExternoNaoRegulado).map(item => {

                return {
                    id: item.id,
                    statusId: item.status,
                    dataAgendada: item.dataAgendada ? this.others.moment(item.dataAgendada).format('DD/MM/YYYY') : null,
                    unidadeExecutoraId: item.unidadeExecutora,
                    ocupacaoExecutoraId: item.ocupacaoExecutora ? item.ocupacaoExecutora.id : null,
                    usuarioExecutorNome: item.usuarioExecutor,
                }
            })

            return this.camelObjectKeysToSnakeRecursive({ solicitacoes })
        }
    },
    mixins: [repository, error, formats, file, stringCase],
}
</script>